import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ViewChild, OnChanges, SimpleChanges } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { MatDateRangePicker } from '@angular/material/datepicker';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { LanguageService } from 'src/shared/services/language.service';
import { UserNotificationService } from 'src/shared/services/user.notification.service';
import { Subscription } from 'rxjs';
import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { ViewEncapsulation } from '@angular/core';

export const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'atom-date-range',
  templateUrl: './date-range.component.html',
  styleUrls: ['./date-range.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
  ],
  encapsulation: ViewEncapsulation.None,
})
export class DateRangeComponent implements OnInit, OnDestroy, OnChanges {
  maxDate: Date | null = new Date();
  range!: FormGroup;
  private langSubscription: Subscription;
  private translateSubscription?: Subscription;
  private _initialRange: { start: Date; end: Date } | null = null;
  private _title: string = '';
  translatedTitle: string = '';

  @Input()
  get title(): string {
    return this._title;
  }
  set title(value: string) {
    this._title = value;
    this.updateTitleTranslation();
  }

  @Input() disableFutureDates: boolean = false;
  @Input() maxMonthsRange?: number; // Cantidad máxima de meses permitidos en el rango
  @Input() set rangeValue(val: { start: Date; end: Date } | null) {
    if (!val) return;
    if (this.range) {
      this.range.patchValue(val);
    } else {
      this._initialRange = val;
    }
  }
  @Output() dateRangeSelected = new EventEmitter<{ start: Date | null, end: Date | null }>();
  @Output() rangeChange = new EventEmitter<{ startDate: string, endDate: string }>();
  @ViewChild('picker') picker!: MatDateRangePicker<any>;

  constructor(
    private dateAdapter: DateAdapter<any>,
    private languageService: LanguageService,
    private translateService: TranslateService,
    private userNotificationService: UserNotificationService
  ) {
    this.langSubscription = this.languageService.currentLanguage$.subscribe(lang => {
      this.dateAdapter.setLocale(lang);
      moment.locale(lang);
      this.updateTitleTranslation();
    });

    const currentLang = this.languageService.getCurrentLanguage();
    this.dateAdapter.setLocale(currentLang);
    moment.locale(currentLang);
  }

  ngOnInit() {
    this.range = new FormGroup({
      start: new FormControl<Date | null>(null),
      end: new FormControl<Date | null>(null),
    });

    this.updateTitleTranslation();

    if (this._initialRange) {
      this.range.patchValue({
        start: this._initialRange.start,
        end:   this._initialRange.end
      });
      this._initialRange = null;
    }

    this.range.valueChanges.subscribe((value) => {
      this.dateRangeSelected.emit({
        start: value.start,
        end: value.end
      });
    });
  }

    ngOnChanges(changes: SimpleChanges) {
      if (changes['disableFutureDates']) {
        this.updateMaxDate();
      }
    }

  onDatePickerClosed(): void {
    const value = this.range.value;

    if (value.start && value.end) {
      if (moment(value.start).isSameOrBefore(moment(value.end))) {
        // Validar que el rango no exceda el máximo de meses permitidos
        if (this.maxMonthsRange) {
          const startMoment = moment(value.start);
          const endMoment = moment(value.end);
          const monthsDiff = endMoment.diff(startMoment, 'months', true);

          if (monthsDiff > this.maxMonthsRange) {
            // Si excede el límite, resetear el rango y no emitir
            this.range.setValue({ start: null, end: null });
            // Mostrar mensaje de error al usuario usando el servicio de notificaciones
            this.userNotificationService.showWarning(
              'MESSAGES.El rango de fechas excede el limite',
              { months: this.maxMonthsRange }
            );
            return;
          }
        }

        const startDate = moment(value.start).format('DD/MM/YYYY');
        const endDate = moment(value.end).format('DD/MM/YYYY');
        this.rangeChange.emit({
          startDate,
          endDate
        });
      }
    }
  }

  reset(): void {
    this.range.setValue({ start: null, end: null });
    this.dateRangeSelected.emit({ start: null, end: null });
    this.rangeChange.emit({ startDate: '', endDate: '' });
  }

  ngOnDestroy() {
    if (this.langSubscription) {
      this.langSubscription.unsubscribe();
    }
    if (this.translateSubscription) {
      this.translateSubscription.unsubscribe();
    }
  }

  private updateMaxDate() {
    this.maxDate = this.disableFutureDates ? new Date() : null;
  }

  private updateTitleTranslation() {
    if (this.translateSubscription) {
      this.translateSubscription.unsubscribe();
    }

    if (this._title && this._title.trim() !== '') {
      this.translateSubscription = this.translateService.get(this._title).subscribe(translation => {
        this.translatedTitle = translation;
      });
    } else {
      this.translatedTitle = '';
    }
  }
}
